<template>
  <div class="blog">
    <header class="blog__nav">
      <div class="blog__nav-inner">
        <router-link to="/" class="blog__brand">
          <span class="blog__brand-mark">S</span>
          <strong>Sauda</strong>
        </router-link>
        <div class="blog__nav-actions">
          <LanguageSwitcher />
          <router-link to="/kassa-login" class="blog__btn blog__btn--ghost">{{ $t('public.common.login') }}</router-link>
          <router-link to="/register" class="blog__btn blog__btn--solid">{{ $t('public.common.startFree') }}</router-link>
        </div>
      </div>
    </header>

    <section class="blog__hero">
      <router-link to="/" class="blog__back">
        <i class="mdi mdi-arrow-left"></i>
        {{ $t('public.common.backHome') }}
      </router-link>
      <h1 class="reveal">{{ $t('public.blog.title') }}</h1>
      <p class="reveal" style="--d: 0.08s">{{ $t('public.blog.subtitle') }}</p>
    </section>

    <section class="blog__list">
      <router-link
        v-for="(post, index) in blogPosts"
        :key="post.slug"
        :to="`/blog/${post.slug}`"
        class="blog__card reveal"
        :style="{ '--d': `${(index % 4) * 0.08}s` }"
      >
        <span class="blog__card-category">{{ post.category }}</span>
        <h2>{{ post.title }}</h2>
        <p>{{ post.excerpt }}</p>
        <div class="blog__card-meta">
          <span><i class="mdi mdi-clock-outline"></i> {{ post.readMinutes }} {{ $t('public.common.minutes') }}</span>
          <span class="blog__card-arrow">{{ $t('public.common.read') }} <i class="mdi mdi-arrow-right"></i></span>
        </div>
      </router-link>
    </section>

    <section class="blog__cta">
      <div class="blog__cta-inner reveal">
        <h2>{{ $t('public.blog.ready') }}</h2>
        <p>{{ $t('public.blog.tryText') }}</p>
        <router-link to="/register" class="blog__btn blog__btn--big blog__btn--light">
          {{ $t('public.common.startFree') }}
          <i class="mdi mdi-arrow-right"></i>
        </router-link>
      </div>
    </section>

    <footer class="blog__footer">
      <router-link to="/" class="blog__brand">
        <span class="blog__brand-mark">S</span>
        <strong>Sauda</strong>
      </router-link>
      <nav class="blog__footer-links">
        <router-link to="/">{{ $t('public.common.backHome') }}</router-link>
        <router-link to="/blog">{{ $t('public.common.blog') }}</router-link>
        <router-link to="/register">{{ $t('public.common.startFree') }}</router-link>
      </nav>
      <p>{{ $t('public.common.footer', { year: new Date().getFullYear() }) }}</p>
    </footer>
  </div>
</template>

<script>
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { getBlogPosts } from '@/data/blogPosts'

export default {
  name: 'BlogPage',
  components: { LanguageSwitcher },
  computed: {
    blogPosts() {
      return getBlogPosts(this.$i18n.locale)
    },
  },
  mounted() {
    const show = (el) => {
      const delay = parseFloat(el.style.getPropertyValue('--d')) || 0
      setTimeout(() => {
        el.classList.add('reveal--in')
      }, delay * 1000)
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              show(entry.target)
              this.observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.15 }
      )
      this.$el.querySelectorAll('.reveal').forEach((el) => this.observer.observe(el))
    } else {
      this.$el.querySelectorAll('.reveal').forEach((el) => el.classList.add('reveal--in'))
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
}
</script>

<style scoped lang="scss">
$ink: #152033;
$muted: #6b7a90;
$accent: #0b4f8a;
$dark: #10243a;

.blog {
  min-height: 100vh;
  background: #f6f9fc;
  color: $ink;
  font-size: 16px;
  overflow-x: hidden;
}

.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition:
    opacity 0.65s cubic-bezier(0.22, 0.8, 0.3, 1),
    transform 0.3s ease;

  &--in {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}

/* ---------- Топбар ---------- */
.blog__nav {
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e3ebf3;
}

.blog__nav-inner {
  max-width: 1160px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.blog__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: $ink;

  strong {
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }
}

.blog__brand-mark {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #163a52, $accent);
  color: #fff;
  font-weight: 800;
  font-size: 18px;
}

.blog__nav-actions {
  display: flex;
  gap: 10px;
}

/* ---------- Кнопки ---------- */
.blog__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border: 0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.18s ease;

  &--solid {
    background: $accent;
    color: #fff;

    &:hover {
      background: #093f6d;
    }
  }

  &--ghost {
    background: transparent;
    color: $ink;

    &:hover {
      background: #e9f1f8;
    }
  }

  &--big {
    padding: 15px 26px;
    font-size: 16px;
    border-radius: 14px;

    .mdi {
      font-size: 18px;
      transition: transform 0.18s ease;
    }

    &:hover .mdi {
      transform: translateX(4px);
    }
  }

  &--light {
    background: #fff;
    color: $accent;

    &:hover {
      background: #eef4fa;
      transform: translateY(-1px);
    }
  }
}

/* ---------- Заголовок ---------- */
.blog__hero {
  max-width: 900px;
  margin: 0 auto;
  padding: 56px 20px 20px;
  text-align: center;
}

.blog__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  color: $muted;
  font-size: 14px;
  font-weight: 650;
  text-decoration: none;
  transition: color 0.18s ease;

  &:hover {
    color: $accent;
  }

  .mdi {
    font-size: 16px;
  }
}

.blog__hero h1 {
  margin: 0 0 12px;
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.blog__hero p {
  margin: 0;
  color: $muted;
  font-size: 16px;
  line-height: 1.6;
}

/* ---------- Список статей ---------- */
.blog__list {
  max-width: 1160px;
  margin: 0 auto;
  padding: 32px 20px 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.blog__card {
  display: flex;
  flex-direction: column;
  padding: 26px;
  background: #fff;
  border: 1px solid #e3ebf3;
  border-radius: 18px;
  text-decoration: none;
  color: $ink;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: #bcd2e4;
    box-shadow: 0 18px 40px rgba(18, 32, 51, 0.12);
  }

  &:hover .blog__card-arrow {
    color: #093f6d;
  }

  &:hover .blog__card-arrow .mdi {
    transform: translateX(4px);
  }

  h2 {
    margin: 14px 0 8px;
    font-size: 19px;
    font-weight: 750;
    line-height: 1.35;
  }

  > p {
    margin: 0;
    color: $muted;
    font-size: 14px;
    line-height: 1.6;
    flex-grow: 1;
  }
}

.blog__card-category {
  display: inline-block;
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 999px;
  background: #e9f1f8;
  color: $accent;
  font-size: 12px;
  font-weight: 700;
}

.blog__card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eef2f6;
  font-size: 13px;
  color: $muted;

  .mdi {
    font-size: 15px;
    vertical-align: -1px;
  }
}

.blog__card-arrow {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $accent;
  font-weight: 700;
  transition: color 0.18s ease;

  .mdi {
    font-size: 15px;
    transition: transform 0.18s ease;
  }
}

/* ---------- CTA ---------- */
.blog__cta {
  padding: 32px 20px 72px;
}

.blog__cta-inner {
  max-width: 1160px;
  margin: 0 auto;
  padding: 52px 32px;
  border-radius: 26px;
  text-align: center;
  color: #fff;
  background:
    radial-gradient(circle at 15% 20%, rgba(90, 168, 220, 0.3), transparent 40%),
    linear-gradient(135deg, #143252, $dark);

  h2 {
    margin: 0 0 10px;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0 0 26px;
    opacity: 0.82;
    font-size: 15.5px;
  }
}

/* ---------- Футер ---------- */
.blog__footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 28px 20px 36px;
  border-top: 1px solid #e3ebf3;

  p {
    margin: 0;
    color: $muted;
    font-size: 13px;
    text-align: center;
  }
}

.blog__footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px;

  a {
    font-size: 13.5px;
    font-weight: 650;
    color: $muted;
    text-decoration: none;
    transition: color 0.18s ease;

    &:hover {
      color: $accent;
    }
  }
}

/* ---------- Адаптив ---------- */
@media (max-width: 760px) {
  .blog__list {
    grid-template-columns: 1fr;
  }

  .blog__hero h1 {
    font-size: 28px;
  }
}

@media (max-width: 600px) {
  .blog__nav-inner {
    padding: 10px 14px;
  }

  .blog__btn--ghost {
    display: none;
  }

  .blog__cta-inner {
    padding: 38px 20px;

    h2 {
      font-size: 24px;
    }
  }
}
</style>
